import { sdkRequest } from '@eventbrite/http';
import React, { createContext, useContext, useState } from 'react';

export type TicketContextProviderProps = {
    children: React.ReactNode;
    ticketServerData: TicketContextType;
};

export type TicketContextType = {
    compactCheckoutDisqualifications: {
        discount_code: boolean;
        has_addons: boolean;
        has_discounts: boolean;
        has_donations: boolean;
        has_inventory_tiers: boolean;
        has_waiting_room: boolean;
        has_waitlist: boolean;
        is_canceled: boolean;
        is_postponed: boolean;
        is_rsvp: boolean;
        is_sales_ended: boolean;
        more_than_one_ticket: boolean;
        reserved_seating: boolean;
        sold_out: boolean;
    };
    panelDisplayPrice: string;
};

export const TicketContext = createContext<{
    ticketData: TicketContextType;
    setTicketData: React.Dispatch<React.SetStateAction<TicketContextType>>;
    fetchDataAndUpdateTicketData: (eventID: string) => void; // Add this line
    isFetchComplete: boolean;
} | null>(null);

export const useTicketContext = () => {
    const context = useContext(TicketContext);
    if (!context)
        throw new Error(
            'TicketContext has to be used within TicketContextProvider',
        );
    return context;
};

export const fetchTicketExpressCheckoutInfo = async (eventID: string) => {
    const apiUrl = `/child_event/${eventID}/compact_checkout_details`;

    try {
        const response = await sdkRequest(apiUrl);
        if (response) return response;
        return null;
    } catch (error) {
        console.error('Error while doing API call:', error);
        throw error;
    }
};

export const TicketContextProvider: React.FC<TicketContextProviderProps> = ({
    children,
    ticketServerData,
}) => {
    const initialState: TicketContextType = ticketServerData;

    const [ticketData, setTicketData] = useState(initialState);
    const [isFetchComplete, setIsFetchComplete] = useState(true);

    const fetchDataAndUpdateTicketData = async (eventID: string) => {
        setIsFetchComplete(false);
        try {
            const response = await fetchTicketExpressCheckoutInfo(eventID);
            const expressCheckoutResponse = {
                compactCheckoutDisqualifications:
                    response?.compact_checkout_disqualifications,
                panelDisplayPrice: response?.panel_display_price,
            };
            setTicketData(expressCheckoutResponse);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setIsFetchComplete(true);
        }
    };

    return (
        <TicketContext.Provider
            value={{
                ticketData,
                setTicketData,
                fetchDataAndUpdateTicketData,
                isFetchComplete,
            }}
        >
            {children}
        </TicketContext.Provider>
    );
};
